import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Box, Typography, TextField, ToggleButton, ToggleButtonGroup, IconButton } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { SectionFieldDTO, FieldEntryDTO } from '../magic-report.type';
import { SINGLE_SELECT_TOGGLE_SX } from './sections.style';

export interface TestData {
  testName: string;
  everReceived: 'Yes' | 'No';
  anyCurrent: 'Yes' | 'No';
  failedTests: string;
}

export const DEFAULT_TESTS: TestData[] = [
  { testName: 'Concrete Strength', everReceived: 'No', anyCurrent: 'No', failedTests: '' },
  { testName: 'Field Density / Soil Strength', everReceived: 'No', anyCurrent: 'No', failedTests: '' },
  { testName: 'Reinforcing Inspections', everReceived: 'No', anyCurrent: 'No', failedTests: '' },
  { testName: 'Post-Tension Stress', everReceived: 'No', anyCurrent: 'No', failedTests: '' },
  { testName: 'Welds', everReceived: 'No', anyCurrent: 'No', failedTests: '' },
  { testName: 'Bolt Torque', everReceived: 'No', anyCurrent: 'No', failedTests: '' },
  { testName: 'Pier Observations', everReceived: 'No', anyCurrent: 'No', failedTests: '' },
];

const GRID_TEMPLATE = '2.3fr 1fr 1fr 3fr 0.8fr';

export const parseTestData = (value: string): TestData | null => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

export const serializeTestData = (test: TestData): string => {
  return JSON.stringify(test);
};

interface MaterialTestingTableProps {
  testsField: SectionFieldDTO | undefined;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  onTestsChange?: (tests: TestData[]) => void; // Callback to notify parent of test changes
  requireTestingField?: SectionFieldDTO;
  onRequireTestingChange?: (value: 'Yes' | 'No') => void;
  isRequireTestingToggleEnabled?: boolean;
  remainingTestsPassedField?: SectionFieldDTO;
  onRemainingTestsPassedChange?: (value: 'Yes' | 'No') => void;
  engineerRespondingField?: SectionFieldDTO;
  onEngineerRespondingChange?: (value: 'Yes' | 'No') => void;
}

export const MaterialTestingTable: React.FC<MaterialTestingTableProps> = ({
  testsField,
  handleFieldChange,
  onTestsChange,
  requireTestingField,
  onRequireTestingChange,
  isRequireTestingToggleEnabled = false,
  remainingTestsPassedField,
  onRemainingTestsPassedChange,
  engineerRespondingField,
  onEngineerRespondingChange,
}) => {
  const testsEntries = testsField?.entries || [];

  const tests: TestData[] = useMemo(() => {
    if (testsEntries.length === 0) {
      return DEFAULT_TESTS;
    }
    return testsEntries.map(entry => parseTestData(entry.value || '')).filter((test): test is TestData => test !== null);
  }, [testsEntries]);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingTest, setEditingTest] = useState<TestData | null>(null);
  const [newTestName, setNewTestName] = useState('');
  const hasInitializedRef = useRef(false);
  const [editingFailedTestsIndex, setEditingFailedTestsIndex] = useState<number | null>(null);
  const [failedTestsDraft, setFailedTestsDraft] = useState('');

  const updateTests = (updatedTests: TestData[]) => {
    const entries: FieldEntryDTO[] = updatedTests.map((test, index) => ({
      value: serializeTestData(test),
      addToActionItems: false,
      order: index,
    }));

    handleFieldChange('MaterialTestingTests', { entries });
    if (onTestsChange) {
      onTestsChange(updatedTests);
    }
  };

  const handleEditTest = (index: number) => {
    setEditingIndex(index);
    setEditingTest({ ...tests[index] });
  };

  const handleSaveEdit = () => {
    if (editingIndex === null || !editingTest) return;

    const updatedTests = [...tests];
    updatedTests[editingIndex] = editingTest;
    updateTests(updatedTests);

    setEditingIndex(null);
    setEditingTest(null);
  };

  const handleCancelEdit = () => {
    // If canceling edit on a row with empty testName, delete it
    if (editingIndex !== null && editingTest && !editingTest.testName.trim()) {
      handleDeleteTest(editingIndex);
    }
    setEditingIndex(null);
    setEditingTest(null);
  };

  const handleDeleteTest = (index: number) => {
    const updatedTests = tests.filter((_, i) => i !== index);
    updateTests(updatedTests);
  };

  const handleTestChange = (index: number, updates: Partial<TestData>) => {
    const updatedTests = [...tests];
    const currentTest = updatedTests[index];
    const newTest = { ...currentTest, ...updates };

    // If "Any Current?" is set to "Yes", automatically set "Ever Received?" to "Yes"
    if (updates.anyCurrent === 'Yes') {
      newTest.everReceived = 'Yes';
    }
    // If "Ever Received?" is set to "No", automatically set "Any Current?" to "No"
    if (updates.everReceived === 'No') {
      newTest.anyCurrent = 'No';
    }

    updatedTests[index] = newTest;
    updateTests(updatedTests);
  };

  const handleStartFailedTestsEdit = (index: number) => {
    setEditingFailedTestsIndex(index);
    setFailedTestsDraft(tests[index].failedTests);
  };

  const handleSaveFailedTestsEdit = () => {
    if (editingFailedTestsIndex === null) return;
    handleTestChange(editingFailedTestsIndex, { failedTests: failedTestsDraft });
    setEditingFailedTestsIndex(null);
  };

  const handleCancelFailedTestsEdit = () => {
    setEditingFailedTestsIndex(null);
  };

  // Initialize tests if empty (only once)
  useEffect(() => {
    if (!hasInitializedRef.current && testsEntries.length === 0) {
      const entries: FieldEntryDTO[] = DEFAULT_TESTS.map((test, index) => ({
        value: serializeTestData(test),
        addToActionItems: false,
        order: index,
      }));
      handleFieldChange('MaterialTestingTests', { entries });
      hasInitializedRef.current = true;
    }
  }, [testsEntries.length, handleFieldChange]);

  const requireTestingValue = requireTestingField?.value === 'Yes' ? 'Yes' : 'No';
  const remainingTestsPassedValue = remainingTestsPassedField?.value === 'No' ? 'No' : 'Yes';
  const engineerRespondingValue = engineerRespondingField?.value === 'No' ? 'No' : 'Yes';
  const hasFailedTests = tests.some(test => test.failedTests && test.failedTests.trim() !== '');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Tests grid */}
      <Box sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: GRID_TEMPLATE,
            alignItems: 'center',
            bgcolor: 'grey.100',
            px: 2,
            py: 1.5,
            columnGap: 2,
          }}
        >
          <Typography variant='body2' fontWeight='medium'>
            Expected Tests
          </Typography>
          <Typography variant='body2' fontWeight='medium' sx={{ textAlign: 'center' }}>
            Ever Received?
          </Typography>
          <Typography variant='body2' fontWeight='medium' sx={{ textAlign: 'center' }}>
            Any Current?
          </Typography>
          <Typography variant='body2' fontWeight='medium'>
            Failed Tests
          </Typography>
          <Typography variant='body2' fontWeight='medium' sx={{ textAlign: 'center' }}>
            Actions
          </Typography>
        </Box>

        {tests.map((test, index) => {
          const isEditing = editingIndex === index && editingTest;
          const hasFailedTestText = test.failedTests && test.failedTests.trim() !== '';

          return (
            <Box
              key={index}
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID_TEMPLATE,
                alignItems: 'center',
                columnGap: 2,
                px: 2,
                py: 1.5,
                bgcolor: index % 2 === 0 ? 'grey.50' : 'background.paper',
                borderTop: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Box>
                {isEditing ? (
                  <TextField
                    size='small'
                    fullWidth
                    value={editingTest.testName}
                    onChange={e => setEditingTest({ ...editingTest, testName: e.target.value })}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSaveEdit();
                    }}
                  />
                ) : (
                  <Typography variant='body2'>{test.testName}</Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {isEditing ? (
                  <ToggleButtonGroup
                    exclusive
                    size='small'
                    value={editingTest.everReceived}
                    onChange={(_, value) => {
                      if (value) {
                        const updated = { ...editingTest, everReceived: value };
                        // If "Ever Received?" is set to "No", automatically set "Any Current?" to "No"
                        if (value === 'No') {
                          updated.anyCurrent = 'No';
                        }
                        setEditingTest(updated);
                      }
                    }}
                    sx={SINGLE_SELECT_TOGGLE_SX}
                  >
                    <ToggleButton value='Yes'>Yes</ToggleButton>
                    <ToggleButton value='No'>No</ToggleButton>
                  </ToggleButtonGroup>
                ) : (
                  <ToggleButtonGroup
                    exclusive
                    size='small'
                    value={test.everReceived}
                    onChange={(_, value) => value && handleTestChange(index, { everReceived: value })}
                    sx={SINGLE_SELECT_TOGGLE_SX}
                  >
                    <ToggleButton value='Yes'>Yes</ToggleButton>
                    <ToggleButton value='No'>No</ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {isEditing ? (
                  <ToggleButtonGroup
                    exclusive
                    size='small'
                    value={editingTest.anyCurrent}
                    onChange={(_, value) => {
                      if (value) {
                        const updated = { ...editingTest, anyCurrent: value };
                        // If "Any Current?" is set to "Yes", automatically set "Ever Received?" to "Yes"
                        if (value === 'Yes') {
                          updated.everReceived = 'Yes';
                        }
                        setEditingTest(updated);
                      }
                    }}
                    sx={SINGLE_SELECT_TOGGLE_SX}
                  >
                    <ToggleButton value='Yes'>Yes</ToggleButton>
                    <ToggleButton value='No'>No</ToggleButton>
                  </ToggleButtonGroup>
                ) : (
                  <ToggleButtonGroup
                    exclusive
                    size='small'
                    value={test.anyCurrent}
                    onChange={(_, value) => value && handleTestChange(index, { anyCurrent: value })}
                    sx={SINGLE_SELECT_TOGGLE_SX}
                  >
                    <ToggleButton value='Yes'>Yes</ToggleButton>
                    <ToggleButton value='No'>No</ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Box>

              <Box>
                {isEditing ? (
                  <TextField
                    size='small'
                    fullWidth
                    multiline
                    minRows={1}
                    value={editingTest.failedTests}
                    onChange={e => setEditingTest({ ...editingTest, failedTests: e.target.value })}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSaveEdit();
                      }
                    }}
                    placeholder='List any failed tests (separated by semicolons)'
                  />
                ) : editingFailedTestsIndex === index ? (
                  <TextField
                    size='small'
                    fullWidth
                    multiline
                    minRows={1}
                    autoFocus
                    value={failedTestsDraft}
                    onChange={e => setFailedTestsDraft(e.target.value)}
                    onBlur={handleSaveFailedTestsEdit}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSaveFailedTestsEdit();
                      } else if (e.key === 'Escape') {
                        handleCancelFailedTestsEdit();
                      }
                    }}
                    placeholder='List any failed tests (separated by semicolons)'
                  />
                ) : (
                  <Box
                    onDoubleClick={() => handleStartFailedTestsEdit(index)}
                    sx={{
                      minHeight: 24,
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 1,
                      cursor: 'text',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    {hasFailedTestText ? (
                      <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                        {test.failedTests}
                      </Typography>
                    ) : (
                      <Typography variant='body2' sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                        --
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                {isEditing ? (
                  <>
                    <IconButton size='small' onClick={handleSaveEdit} color='primary'>
                      <CheckIcon />
                    </IconButton>
                    <IconButton size='small' onClick={handleCancelEdit} color='error'>
                      <CloseIcon />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <IconButton size='small' onClick={() => handleEditTest(index)} color='primary'>
                      <EditIcon />
                    </IconButton>
                    <IconButton size='small' onClick={() => handleDeleteTest(index)} color='error'>
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Add new test */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          placeholder='Enter test name'
          value={newTestName}
          onChange={e => setNewTestName(e.target.value)}
          onKeyPress={e => {
            if (e.key === 'Enter') {
              if (!newTestName.trim()) return;
              const newTest: TestData = {
                testName: newTestName.trim(),
                everReceived: 'No',
                anyCurrent: 'No',
                failedTests: '',
              };
              const newTests = [...tests, newTest];
              updateTests(newTests);
              setNewTestName('');
            }
          }}
          sx={{ flexGrow: 1 }}
        />
        <IconButton
          onClick={() => {
            if (!newTestName.trim()) return;
            const newTest: TestData = {
              testName: newTestName.trim(),
              everReceived: 'No',
              anyCurrent: 'No',
              failedTests: '',
            };
            const newTests = [...tests, newTest];
            updateTests(newTests);
            setNewTestName('');
          }}
          size='small'
          disabled={!newTestName.trim()}
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: 'primary.main',
              color: 'primary.main',
              transform: 'scale(1.1)',
            },
          }}
        >
          <AddIcon fontSize='small' />
        </IconButton>
      </Box>

      {/* Require testing for retainage release button */}
      {isRequireTestingToggleEnabled && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <Typography variant='body2' sx={{ fontWeight: 500 }}>
            Require testing for retainage release
          </Typography>
          <ToggleButtonGroup
            exclusive
            size='small'
            value={requireTestingValue}
            onChange={(_, value) => {
              if (!value || !onRequireTestingChange) return;
              onRequireTestingChange(value);
            }}
            sx={SINGLE_SELECT_TOGGLE_SX}
          >
            <ToggleButton value='Yes'>Yes</ToggleButton>
            <ToggleButton value='No'>No</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Has remaining testing passed? / Engineer responding? (only when there are failed tests) */}
      {hasFailedTests && (onRemainingTestsPassedChange || onEngineerRespondingChange) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mt: 2, flexWrap: 'wrap' }}>
          {onRemainingTestsPassedChange && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                Has remaining testing passed?
              </Typography>
              <ToggleButtonGroup
                exclusive
                size='small'
                value={remainingTestsPassedValue}
                onChange={(_, value) => {
                  if (!value) return;
                  onRemainingTestsPassedChange(value);
                }}
                sx={SINGLE_SELECT_TOGGLE_SX}
              >
                <ToggleButton value='Yes'>Yes</ToggleButton>
                <ToggleButton value='No'>No</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {onEngineerRespondingChange && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                Engineer responding?
              </Typography>
              <ToggleButtonGroup
                exclusive
                size='small'
                value={engineerRespondingValue}
                onChange={(_, value) => {
                  if (!value) return;
                  onEngineerRespondingChange(value);
                }}
                sx={SINGLE_SELECT_TOGGLE_SX}
              >
                <ToggleButton value='Yes'>Yes</ToggleButton>
                <ToggleButton value='No'>No</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
